import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { ChatDraft, useChat } from "@/contexts/ChatContext";
import { MemberView, MessageView } from "@/interfaces/Chat";
import { plainText } from "@/helpers/chatText";
import {
  useGetChatMembersQuery,
  useGetChatUsersQuery,
  useSendChatMessageMutation,
} from "@/services/chat";
import { chatError } from "./CreateConversationModal";
import MentionInput from "./MentionInput";
import ReplyPreview from "./ReplyPreview";
import ReactionPicker from "./ReactionPicker";
const empty: ChatDraft = { parts: [] };
export default function MessageComposer({
  conversationId,
  canWrite,
  onSent,
}: {
  conversationId: string;
  canWrite: boolean;
  onSent: (message: MessageView) => void;
}) {
  const chat = useChat(),
    { actorId } = chat,
    [send] = useSendChatMessageMutation();
  const draft = chat.drafts[conversationId] || empty,
    text = plainText(draft.parts),
    sending = !!chat.submitting[conversationId];
  const [error, setError] = useState(""),
    [picker, setPicker] = useState(false),
    [search, setSearch] = useState<string | null>(null),
    [cursor, setCursor] = useState<string | undefined>(),
    [pages, setPages] = useState<MemberView[]>([]);
  const scope = useRef<object>({}),
    form = useRef<HTMLFormElement>(null);
  // A normal remount preserves provider-owned submissions. These callbacks only
  // update the mounted actor/conversation that started them.
  useEffect(() => {
    scope.current = {};
    setError("");
    setPicker(false);
    setSearch(null);
    setCursor(undefined);
    setPages([]);
    return () => {
      scope.current = {};
    };
  }, [actorId, conversationId, canWrite]);
  const members = useGetChatMembersQuery(
    {
      actorId: actorId!,
      conversationId,
      q: search || undefined,
      cursor,
      limit: 30,
    },
    { skip: !actorId || !canWrite || search === null }
  );
  const directory = useGetChatUsersQuery(
    { actorId: actorId!, q: search || undefined, limit: 30 },
    { skip: !actorId || !canWrite || search === null }
  );
  useEffect(() => {
    if (members.currentData)
      setPages((previous) =>
        cursor
          ? [
              ...previous,
              ...members.currentData!.data.filter(
                (m) => !previous.some((p) => p.id === m.id)
              ),
            ]
          : members.currentData!.data
      );
  }, [members.currentData, cursor]);
  const update = (next: ChatDraft) => chat.setDraft(conversationId, next);
  const submit = async () => {
    if (
      !actorId ||
      !canWrite ||
      !text.trim() ||
      Array.from(text).length > 10000
    )
      return;
    const body = chat.beginSubmission(conversationId, draft);
    if (!body) return;
    const current = scope.current;
    setError("");
    try {
      const message = await send({ actorId, conversationId, body }).unwrap();
      const accepted = chat.finishSubmission(conversationId, body, true);
      if (accepted && scope.current === current) onSent(message);
    } catch (e) {
      const accepted = chat.finishSubmission(conversationId, body, false);
      if (accepted && scope.current === current) setError(chatError(e));
    }
  };
  const reply =
    draft.reply ||
    (draft.replyToId
      ? chat.messages.find((m) => m.id === draft.replyToId)
      : null);
  const replySnapshot =
    reply &&
    ("authorName" in reply
      ? reply
      : {
          id: reply.id,
          authorName: reply.author.name,
          excerpt: plainText(reply.parts).slice(0, 240),
        });
  return (
    <Form
      ref={form}
      className="chat-composer"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {error && <Alert variant="danger">{error}</Alert>}
      {!canWrite && (
        <p className="chat-read-only">
          Cette conversation est en lecture seule.
        </p>
      )}
      {replySnapshot && (
        <ReplyPreview
          reply={replySnapshot}
          onCancel={() => update({ parts: draft.parts })}
        />
      )}
      <MentionInput
        parts={draft.parts}
        onChange={(parts) => update({ ...draft, parts })}
        disabled={!canWrite}
        onSend={submit}
        members={pages
          .filter((m) => m.active)
          .map((m) => ({
            ...m,
            email: directory.currentData?.data.find(
              (person) => person.id === m.id
            )?.email,
          }))}
        onSearch={(q) => {
          if (q !== search) {
            setPages([]);
            setCursor(undefined);
            setSearch(q);
          }
        }}
        hasMore={!!members.currentData?.nextCursor}
        onMore={() => setCursor(members.currentData?.nextCursor || undefined)}
      />
      <div className="chat-composer-footer">
        <span>Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne</span>
        <div>
          <Button
            variant="light"
            type="button"
            aria-label="Insérer un emoji"
            disabled={!canWrite}
            onClick={() => setPicker(true)}
          >
            ☺
          </Button>{" "}
          <Button
            type="submit"
            disabled={
              !canWrite ||
              sending ||
              !text.trim() ||
              Array.from(text).length > 10000
            }
          >
            {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </div>
      </div>
      {Array.from(text).length > 10000 && (
        <span role="alert">Le message dépasse 10 000 caractères.</span>
      )}
      {picker && (
        <ReactionPicker
          onClose={() => setPicker(false)}
          onSelect={(emoji) => {
            update({
              ...draft,
              parts: [...draft.parts, { type: "text", text: emoji }],
            });
            setPicker(false);
          }}
        />
      )}
    </Form>
  );
}
